// src/pages/FinancialReportingPage.jsx
// Phase 1 only - see MEMORY: financial_reporting_dashboard_scoping. Real current
// snapshot + real per-client breakdown, both already computed by
// businessService.getFinancialAnalysis() (same source Dashboard and AIM both read) -
// no new backend logic. Deliberately does NOT include trend charts, expense-category
// breakdown, or a period selector: none of that data exists anywhere in this system
// today (checked directly - no historical financial snapshots, overhead is one
// aggregate figure/multiplier with no category split, revenue is tracked per-client
// only with no service-line dimension). Building any of those now would mean
// inventing data that isn't real, which this project treats as a fabrication bug
// everywhere else - Phase 2 (periodic snapshot recording, real expense entry,
// service-line schema) is real new infrastructure, scoped as its own future work.
import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Layers, Percent } from 'lucide-react';
import apiService from '../services/apiService';
import { formatCurrency } from '../utils/helpers';
import { theme, rgba } from '../theme';

const STAFFING_COLORS = {
  fully_staffed: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e', label: 'Fully staffed' },
  partial:       { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', label: 'Partial' },
  unassigned:    { bg: 'rgba(239,68,68,0.15)',  color: '#ef4444', label: 'Unassigned' },
};

function SnapshotTile({ icon: Icon, label, value, sub, accentRgb }) {
  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      padding: '24px',
      background: rgba(theme.colors.tileBackgroundRgb, 0.8),
      border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
      borderRadius: '14px',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(135deg, rgba(${accentRgb},0.15), rgba(${accentRgb},0.02))`,
      }} />
      <div style={{ position: 'relative' }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '9px',
          background: `rgba(${accentRgb},0.18)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '12px',
        }}>
          <Icon size={18} color={`rgb(${accentRgb})`} />
        </div>
        <div style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}
        </div>
        <div style={{ color: '#e2e8f0', fontSize: '26px', fontWeight: '700', marginTop: '4px' }}>{value}</div>
        {sub && <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function FinancialReportingPage() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.getBusinessData()
      .then(data => {
        if (data?.success) setAnalysis(data.data?.financialAnalysis || null);
        else setError('Failed to load financial data.');
      })
      .catch(err => setError(err.message || 'Failed to load financial data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '48px', color: '#94a3b8' }}>Loading...</div>;
  if (error) return <div style={{ padding: '48px', color: '#ef4444' }}>{error}</div>;
  if (!analysis) return <div style={{ padding: '48px', color: '#94a3b8' }}>No financial data available yet.</div>;

  const overheadLabel = analysis.overheadMethod === 'flat_expenses'
    ? 'Stated directly'
    : analysis.overheadMethod === 'oer_derived'
      ? 'Derived from OER'
      : analysis.overheadMethod === 'ambiguous_needs_choice'
        ? 'Ambiguous - choose a method in chat'
        : 'Not yet configured';

  return (
    <div style={{ padding: '40px 48px', minHeight: '100vh' }}>
      <h1 style={{ color: '#e2e8f0', fontSize: '26px', margin: '0 0 6px' }}>Financial Reporting</h1>
      <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 32px' }}>
        Current snapshot and per-client breakdown - real figures only, computed live from your business data.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px',
        marginBottom: '40px',
      }}>
        <SnapshotTile
          icon={DollarSign}
          label="Monthly Revenue"
          value={formatCurrency(analysis.totalRevenue)}
          accentRgb="34,197,94"
        />
        <SnapshotTile
          icon={Layers}
          label="Gross Profit"
          value={formatCurrency(analysis.grossProfit)}
          sub={`${analysis.profitMargin?.toFixed(1)}% margin`}
          accentRgb="139,92,246"
        />
        <SnapshotTile
          icon={TrendingUp}
          label="Net Profit"
          value={analysis.netProfit != null ? formatCurrency(analysis.netProfit) : '—'}
          sub={analysis.netProfitMargin != null ? `${analysis.netProfitMargin.toFixed(1)}% margin` : 'Overhead not configured'}
          accentRgb="59,130,246"
        />
        <SnapshotTile
          icon={Percent}
          label="Overhead"
          value={analysis.overheadAmount ? formatCurrency(analysis.overheadAmount) : '—'}
          sub={overheadLabel}
          accentRgb="245,158,11"
        />
      </div>

      <h2 style={{ color: '#e2e8f0', fontSize: '18px', margin: '0 0 16px' }}>Revenue by client</h2>
      <div style={{
        background: rgba(theme.colors.tileBackgroundRgb, 0.8),
        border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.2)}` }}>
              {['Client', 'Revenue', 'Labor Cost', 'Profit', 'Margin', 'Staffing'].map(h => (
                <th key={h} style={{
                  textAlign: h === 'Client' ? 'left' : 'right',
                  padding: '12px 20px',
                  color: '#94a3b8',
                  fontSize: '11px',
                  fontWeight: '700',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(analysis.clientBreakdowns || []).map((c, i) => {
              const staffing = STAFFING_COLORS[c.staffingStatus] || STAFFING_COLORS.unassigned;
              return (
                <tr key={c.clientName} style={{
                  borderTop: i > 0 ? `1px solid ${rgba(theme.colors.tileBorderRgb, 0.1)}` : 'none',
                }}>
                  <td style={{ padding: '14px 20px', color: '#e2e8f0', fontWeight: '600' }}>{c.clientName}</td>
                  <td style={{ padding: '14px 20px', textAlign: 'right', color: '#e2e8f0' }}>{formatCurrency(c.revenue)}</td>
                  <td style={{ padding: '14px 20px', textAlign: 'right', color: '#94a3b8' }}>
                    {c.laborCost != null ? formatCurrency(c.laborCost) : '—'}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'right', color: c.profit >= 0 ? '#22c55e' : '#ef4444' }}>
                    {c.profit != null ? formatCurrency(c.profit) : '—'}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'right', color: '#e2e8f0' }}>
                    {c.marginPercent != null ? `${c.marginPercent.toFixed(1)}%` : '—'}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                    <span style={{
                      background: staffing.bg,
                      color: staffing.color,
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}>
                      {staffing.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(analysis.clientBreakdowns || []).length === 0 && (
          <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>No clients on file yet.</div>
        )}
      </div>
    </div>
  );
}
